import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { useTreeMutations } from 'components/WorkspaceEditor/components/TrackChangesPanel/hooks/TreeMutations/useTreeMutations'
import {
    useNotification,
    useTransactionManager,
} from 'components/WorkspaceEditor/services'
import { useCallback, useEffect, useMemo } from 'react'
import { useShallowSelector } from '../../../Workspace2Store'
import { ContentItemViewNodesType } from '../../../types'
import { tryGetShortcutKey, preventEventDefault } from '../../../utils/shortcutKeyUtils'
import { usePromoteDemoteContentMutation } from '../api/mutations/promoteDemoteContent'
import {
    INCONSISTENT_PARAGRAPH_LEVEL_ERROR,
    NOT_FOUND,
    NO_ACTIVE_ID,
    PromoteDemoteActions,
} from '../constants'
import { usePromoteDemoteSelector } from '../store/PromoteDemoteStore'
import { PromoteDemoteAction } from '../types'
import { createOptimisticTreeState, wouldExceedDepthLimit } from '../utils'
import { useFocusRestoration } from './useFocusRestoration'
import { usePromoteDemoteService } from './usePromoteDemoteService'

/**
 * Hook that encapsulates promote/demote functionality for text content elements.
 *
 * @returns An object containing state and functions for promote/demote operations
 */
export const usePromoteDemote = () => {
    const { buildInputVariables } = usePromoteDemoteService()
    const { executeWithOptimistic } = useTransactionManager()
    const { promoteDemoteContentItem } = usePromoteDemoteContentMutation()
    const { reRenderTree } = useTreeMutations()
    const { restoreFocus } = useFocusRestoration()
    const notification = useNotification()
    const {
        isContentTeamFromStudioPage,
        isEditingPlainText,
        contentItemViewNodes,
        newParagraphToAdd,
        treeData,
        setContentItemViewNodes,
    } = useShallowSelector((s) => ({
        isContentTeamFromStudioPage: s.isContentTeamFromStudioPage,
        isEditingPlainText: s.isEditingPlainText,
        contentItemViewNodes: s.contentItemViewNodes,
        setContentItemViewNodes: s.actions.setContentItemViewNodes,
        newParagraphToAdd: s.textContentElementToAdd,
        treeData: s.treeData,
    }))

    const {
        canBePromoted,
        canBeDemoted,
        contentItemToPromoteDemote: activeItem,
        paragraphLevel,
        setContentItemToPromoteDemote,
        resetPromoteDemoteState: resetPromoteDemote,
    } = usePromoteDemoteSelector((s) => ({
        canBePromoted: s.canBePromoted,
        canBeDemoted: s.canBeDemoted,
        contentItemToPromoteDemote: s.contentItemToPromoteDemote,
        paragraphLevel: s.paragraphLevel,
        setContentItemToPromoteDemote: s.setContentItemToPromoteDemote,
        resetPromoteDemoteState: s.resetPromoteDemoteState,
    }))

    const isCreatingNewParagraph = useMemo(() => Boolean(newParagraphToAdd), [
        newParagraphToAdd,
    ])
    const canPromote =
        !isCreatingNewParagraph && canBePromoted(isContentTeamFromStudioPage)
    const canDemote =
        !isCreatingNewParagraph && canBeDemoted(isContentTeamFromStudioPage)

    // Register content for promote/demote operations
    const register = useCallback(
        (
            contentItem: Partial<TextContentElement>,
            isInProject: boolean = false,
            contentItemViewNodes: Partial<ContentItemViewNodesType>[] = []
        ) => {
            setContentItemToPromoteDemote({
                contentItemToPromoteDemote: contentItem,
                isInProject,
                contentItemViewNodes,
            })
        },
        [setContentItemToPromoteDemote]
    )

    // handle mutation request creation and execution
    const handlePromoteDemoteMutation = useCallback(
        async (action: PromoteDemoteAction) => {
            const activeItemId = activeItem?.id

            if (isCreatingNewParagraph) return
            if (!activeItemId)
                return notification.error({
                    message: `${NO_ACTIVE_ID} ${action}`,
                })
            if (wouldExceedDepthLimit(activeItemId, action, treeData))
                return notification.error(INCONSISTENT_PARAGRAPH_LEVEL_ERROR)

            const variables = buildInputVariables(action)
            if (!variables)
                return notification.error({
                    message: `${NOT_FOUND} ${action}`,
                })

            const optimisticNodes = createOptimisticTreeState(
                contentItemViewNodes,
                activeItemId,
                action,
                paragraphLevel
            )

            // update UI immediately
            setContentItemViewNodes(optimisticNodes)

            executeWithOptimistic({
                fallbackState: contentItemViewNodes,
                asyncAction: () => promoteDemoteContentItem({ variables }),
                successCbFn: () => {
                    resetPromoteDemote()
                    
                    // Restore focus after promote/demote operations
                    // Both operations need focus restoration to maintain cursor position
                    restoreFocus(activeItemId)
                },
                errorCbFn: () => {
                    // revert to previous tree state
                    reRenderTree({
                        updatedContentItemViewNodes: contentItemViewNodes,
                    })
                },
            })
        },
        [
            executeWithOptimistic,
            promoteDemoteContentItem,
            buildInputVariables,
            activeItem,
            contentItemViewNodes,
            paragraphLevel,
            reRenderTree,
            isCreatingNewParagraph,
            treeData,
            notification,
            restoreFocus,
        ]
    )

    // handle keyboard events for promote/demote shortcuts
    const handlePromoteDemoteShortcutKey = useCallback(
        (event: React.KeyboardEvent<HTMLElement>) => {
            const { Promote, Demote } = PromoteDemoteActions

            if (tryGetShortcutKey(Promote, event) && canPromote) {
                // Prevent default Shift+Tab behavior to stop focus from moving
                preventEventDefault(event)
                handlePromoteDemoteMutation(Promote)
            } else if (tryGetShortcutKey(Demote, event) && canDemote) {
                // Prevent default Tab behavior to stop focus from moving to next element
                preventEventDefault(event)
                handlePromoteDemoteMutation(Demote)
            }
        },
        [canPromote, canDemote, handlePromoteDemoteMutation]
    )

    /**
     * side-effect: reset promote/demote state when nothing is selected
     * Toggles the Promote/Demote buttons on action bar
     */
    useEffect(() => {
        let resetId: NodeJS.Timeout

        if (!isEditingPlainText && activeItem) {
            resetId = setTimeout(() => {
                // reset means toggle the promote/demote buttons on action bar
                resetPromoteDemote()
            }, 100) // 100ms delay to ensure that the current item is evaluated by action bar before resetting the state
        }

        return () => clearTimeout(resetId)
    }, [isEditingPlainText, activeItem, resetPromoteDemote])

    return {
        canPromote,
        canDemote,
        activeItem,
        register,
        handlePromoteDemoteShortcutKey,
        handlePromoteDemoteMutation,
        resetPromoteDemote,
    }
}
